import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartSectionRestoredEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { changeAddonParentQuantity, queueCartAddonMutation } from '@theme/custom.cart-addon-links';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.#loadComponentSync();
  }

  /**
   * The module that keeps a design's chain and birthstone lines in step with it loads only once
   * the cart holds a design, so a cart without one pays nothing for it.
   */
  #loadComponentSync() {
    // The same selector as the module's own listener; a literal here, so it stays unloaded.
    if (this.querySelector('tr[data-design]')) {
      void import('@theme/custom.customizer-components');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Re-renders the cart section when the page comes back through the browser's Back button.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart held
   * before the shopper navigated away. `persisted` alone misses Chrome: it refuses the bfcache for
   * any document holding the Sign in with Shop iframe, so Back rebuilds the page from the HTTP
   * cache with `persisted` false and only the navigation entry's `back_forward` type tells it
   * apart from a fresh view. `cache: false` because both paths leave the renderer holding the
   * same stale copy the DOM already shows.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry)
      ? navigationEntry.type
      : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the getter throws
    // without it; this runs straight off a window listener, so the throw would escape.
    if (!this.dataset.sectionId) return;

    sectionRenderer
      .renderSection(this.sectionId, { cache: false, mode: this.#morphMode })
      .then((html) => {
        // An aborted render resolves empty (assets/section-renderer.js): nothing morphed, so
        // there is no restore to announce and nothing to re-measure.
        if (!html) return;
        // Morph swaps the quantity selectors in place, so their min/max button states stay
        // bound to the pre-restore quantities until told otherwise.
        this.#updateCartQuantitySelectorButtonStates();
        // No drawer-open event fires on a restore, so the sticky summary measurement is driven
        // from here. `cart-drawer-component` listens for this.
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#rowForLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector('text-component')
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#rowForLine(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    // A section render already in flight fetched the cart before this change; left running, it
    // would land after the morph below and put the previous quantities back.
    for (const sectionId of sectionsToUpdate) sectionRenderer.abortRender(sectionId);

    const row = this.#rowForLine(line);
    const upsells = row?.querySelector('custom-cart-upsells');
    const addonParent =
      upsells instanceof HTMLElement && upsells.dataset.addonParent ? { ...upsells.dataset } : null;
    // A design's nested chain and stone never follow its quantity, so the stepper moves them in
    // the same request; Shopify removes them with the design on its own.
    const componentKeys =
      row && quantity > 0 ? (row.dataset.componentKeys ?? '').split(' ').filter(Boolean) : [];
    const request = addonParent
      ? queueCartAddonMutation(() =>
          changeAddonParentQuantity(
            addonParent,
            quantity,
            {
              sections: Array.from(sectionsToUpdate).join(','),
              sections_url: window.location.pathname,
            },
            componentKeys
          )
        )
      : row?.dataset.key && componentKeys.length > 0
        ? fetch(
            Theme.routes.cart_update_url,
            fetchConfig('json', {
              body: JSON.stringify({
                updates: Object.fromEntries(
                  [row.dataset.key, ...componentKeys].map((key) => [key, quantity])
                ),
                sections: Array.from(sectionsToUpdate).join(','),
                sections_url: window.location.pathname,
              }),
            })
          )
        : fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));

    request
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // update.js reports a refusal as { status, description }, not { errors }.
        if (
          !parsedResponseText.errors &&
          parsedResponseText.status &&
          parsedResponseText.description
        ) {
          parsedResponseText.errors = parsedResponseText.description;
        }
        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount =
          newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        sectionRenderer.abortRender(this.sectionId);
        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.#morphMode,
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantityInput = this.#rowForLine(line)?.querySelector('.quantity-selector input');

    if (!(quantityInput instanceof HTMLInputElement)) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    // Every re-render below goes through #morphMode: mutations raised outside this component
    // (upsell tile, standard-actions bridge, product form) can land while the drawer is open.
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.#morphMode });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      sectionRenderer.abortRender(this.sectionId);
      morphSection(this.sectionId, cartItemsHtml, { mode: this.#morphMode });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#loadComponentSync();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.#morphMode });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    // One variant can sit on several lines (a split discount, or designs with different
    // personalizations), so each selector gets the variant's total, not its last line.
    /** @type {Map<string, number>} */
    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) ?? 0) + item.quantity);
    }

    for (const [variantId, quantity] of quantitiesByVariant) {
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Finds the rendered row for a 1-based cart line number.
   *
   * By `data-line`, never by position: inlined upsell lines render no row of
   * their own (snippets/cart-products.liquid), so a row's array index and its
   * cart line number can diverge.
   *
   * @param {number} line - The line item index.
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowForLine(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.line === String(line));
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`; `entryType`
   * is the spec's discriminant and lives on the base, so this narrows at runtime instead of casting.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }

  /**
   * How much of the section a cart re-render is allowed to replace.
   *
   * In the drawer the section is `custom-cart-drawer`, whose stand-in wrapper sits inside the
   * top-layer `<dialog>`. Hydration mode still confines the morph to
   * `[data-hydration-key="cart-drawer-inner"]`: if the section ever spans the dialog again (it used
   * to be the whole header), a full morph would re-insert an ancestor of the dialog, evict it from
   * the top layer and strand it with `open` set, so `showDialog()` never re-opens it modally.
   *
   * KEY-DECISION 2026-07-31: keyed on `isDrawer`, not on `dialog.open` - a closed drawer that
   * full-morphs still leaves the two paths free to diverge the next time markup drifts.
   *
   * @returns {'hydration' | 'full'} The morph mode.
   */
  get #morphMode() {
    return this.isDrawer ? 'hydration' : 'full';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
